import { useEffect, useState } from 'react';

/** Human-readable "N min/hour/day ago". */
export function formatRelative(iso?: string | null): string {
  if (!iso) return 'ещё не запускался';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 0) return 'только что';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec} сек назад`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} мин назад`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} ч ${min % 60} мин назад`;
  const days = Math.floor(hr / 24);
  return `${days} дн назад`;
}

/**
 * The scheduler in BullMQ fires on cron `0 *​/2 * * *` — every even UTC hour
 * at minute 0. Compute the next firing relative to the user's wall clock.
 */
export function formatNextScheduled(): string {
  const now = new Date();
  const next = new Date(now);
  next.setUTCMinutes(0, 0, 0);
  const curUtcH = now.getUTCHours();
  let nextH = curUtcH + (curUtcH % 2 === 0 ? 2 : 1);
  if (nextH >= 24) {
    next.setUTCDate(next.getUTCDate() + 1);
    nextH -= 24;
  }
  next.setUTCHours(nextH);
  const diffMin = Math.max(1, Math.round((next.getTime() - now.getTime()) / 60000));
  if (diffMin < 60) return `через ${diffMin} мин`;
  const hr = Math.floor(diffMin / 60);
  const mn = diffMin % 60;
  return `через ${hr} ч ${mn} мин`;
}

/** Re-renders the host component every minute so "N min ago" / "in N min" stay fresh. */
export function useMinuteTicker() {
  const [, set] = useState(0);
  useEffect(() => {
    const t = setInterval(() => set((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
}

/**
 * Local 1Hz countdown driven off the server-provided absolute deadline
 * (`waitUntilMs`) and initial duration (`waitMs`). Returns null when the
 * config isn't in the waiting phase — callers can then show their default
 * message. Stops the interval as soon as the phase changes.
 */
export function useWaitingCountdown(progress: any | null | undefined) {
  const waitUntilMs: number | undefined = progress?.details?.waitUntilMs;
  const waitMs: number | undefined = progress?.details?.waitMs;
  const isWaiting = progress?.phase === 'waiting' && !!waitUntilMs;

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!isWaiting) return;
    setNowMs(Date.now());
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isWaiting, waitUntilMs]);

  if (!isWaiting) return { secLeft: null as number | null, pct: 0, totalSec: 0 };
  const secLeft = Math.max(0, Math.ceil((waitUntilMs! - nowMs) / 1000));
  const pct = waitMs
    ? Math.min(100, Math.max(0, 100 - ((waitUntilMs! - nowMs) / waitMs) * 100))
    : 0;
  const totalSec = waitMs ? Math.round(waitMs / 1000) : 0;
  return { secLeft, pct, totalSec };
}
